import { AdminChapter, AdminChapterStatus, AdminChapterTest, AdminLesson, AdminModule, NewAdminChapter } from 'types/admin/modules';
import { create } from 'zustand';

export type ChaptersData = Pick<AdminModule, 'chapters'>;
type State = ChaptersData & { initializing: boolean; loading: boolean };

type Actions = {
  setInitialChapters: (chapters: AdminChapter[]) => void;
  addChapter: () => void;
  deleteChapter: (index: number) => void;
  updateChapterTitle: (index: number, title: string) => void;
  updateChapterStatus: (index: number, status: AdminChapterStatus) => void;
  addLesson: (index: number, newLessons: AdminLesson[]) => void;
  deleteLesson: (chapterIndex: number, lessonIndex: number) => void;
  addChapterTest: (index: number, chapterTest: AdminChapterTest) => void;
  updateLesson: (chapterIndex: number, lessonIndex: number, updatedLesson: AdminLesson) => void;
  updateChapterTest: (index: number, chapterTest: AdminChapterTest) => void;
  sortChapters: (from: number, to: number) => void;
  sortLessons: (chapterIndex: number, from: number, to: number) => void;
  reset: () => void;
  setLoadingInitialData: (initializing: boolean) => void;
  setLoading: (loading: boolean) => void;
};

export const useChaptersStore = create<State & Actions>((set, get, store) => ({
  chapters: [],
  initializing: false,
  loading: false,
  setInitialChapters: (chapters: AdminChapter[]) => set((state: State) => ({ chapters })),
  addChapter: () => set((state: State) => ({ chapters: [...state.chapters, emptyChapter] })),
  deleteChapter: (index: number) => set((state: State) => ({ chapters: state.chapters.filter((chapter, i) => i !== index) })),
  updateChapterTitle: (index: number, title: string) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, title })) })),
  updateChapterStatus: (index: number, status: AdminChapterStatus) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, status })) })),
  addLesson: (index: number, newLessons: AdminLesson[]) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, lessons: [...chapter.lessons, ...newLessons] })),
    })),
  deleteLesson: (chapterIndex: number, lessonIndex: number) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, i) =>
        i !== chapterIndex ? chapter : { ...chapter, lessons: chapter.lessons.filter((lesson, index) => lessonIndex !== index) }
      ),
    })),
  addChapterTest: (index: number, chapterTest: AdminChapterTest) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, chapterTest })) })),
  updateLesson: (chapterIndex: number, lessonIndex: number, updatedLesson: AdminLesson) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, i) =>
        i !== chapterIndex
          ? chapter
          : { ...chapter, lessons: chapter.lessons.map((lesson, i) => (i !== lessonIndex ? lesson : updatedLesson)) }
      ),
    })),
  updateChapterTest: (index: number, chapterTest: AdminChapterTest) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, chapterTest })) })),
  sortChapters: (from: number, to: number) => set((state: State) => ({ chapters: moveItems(state.chapters, from, to) as AdminChapter[] })),
  sortLessons: (chapterIndex: number, from: number, to: number) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, index) =>
        index !== chapterIndex ? chapter : { ...chapter, lessons: moveItems(chapter.lessons, from, to) as AdminLesson[] }
      ),
    })),
  reset: () => set(store.getInitialState),
  setLoadingInitialData: (initializing: boolean) => set(() => ({ initializing })),
  setLoading: (loading: boolean) => set(() => ({ loading })),
}));

const emptyChapter: NewAdminChapter = { title: '', status: 'Hidden', lessons: [] };

const moveItems = (array: AdminChapter[] | AdminLesson[], from: number, to: number) => {
  const newArr = [...array];
  const item = newArr.splice(from, 1)[0];
  newArr.splice(to, 0, item);

  return newArr;
};
